import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";

export const SOCKET_RECOVER_EVENT = "whaticket:socket-recover";

const RECOVER_COOLDOWN_MS = 2500;
const FORCE_RECONNECT_HIDDEN_MS = 5000;

let socket = null;
let visibilityHandlerAttached = false;
let hadDisconnect = false;
let hiddenAt = null;
let lastRecoverAt = 0;
let managerListenersAttached = false;

function socketLog(...args) {
	if (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production") {
		return;
	}
	// eslint-disable-next-line no-console
	console.log("[socket]", ...args);
}

export function getStoredToken() {
	const raw = localStorage.getItem("token");
	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw);
		return typeof parsed === "string" && parsed.length > 0 ? parsed : null;
	} catch {
		return raw.length > 0 ? raw : null;
	}
}

export function syncSocketAuthToken(token = getStoredToken()) {
	if (!socket || !token) {
		return;
	}

	const query = { ...(socket.io?.opts?.query || {}), token };
	if (socket.io && socket.io.opts) {
		socket.io.opts.query = query;
	}
	if (socket.io && socket.io.engine && socket.io.engine.opts) {
		socket.io.engine.opts.query = query;
	}
}

function dispatchRecover(reason) {
	const now = Date.now();
	if (now - lastRecoverAt < RECOVER_COOLDOWN_MS) {
		return;
	}
	lastRecoverAt = now;

	socketLog("recover", reason);
	if (typeof window !== "undefined") {
		window.dispatchEvent(
			new CustomEvent(SOCKET_RECOVER_EVENT, { detail: { reason } })
		);
	}
}

function attachManagerListeners(activeSocket) {
	if (managerListenersAttached) {
		return;
	}
	managerListenersAttached = true;

	activeSocket.on("disconnect", reason => {
		hadDisconnect = true;
		socketLog("disconnect", reason);
	});

	activeSocket.on("connect", () => {
		syncSocketAuthToken();
		if (hadDisconnect) {
			hadDisconnect = false;
			dispatchRecover("reconnect");
		} else {
			socketLog("connected");
		}
	});
}

function forceReconnect(reason) {
	if (!socket || !getStoredToken()) {
		return;
	}

	syncSocketAuthToken();
	hadDisconnect = true;
	socketLog("forcing reconnect", reason);

	try {
		socket.disconnect();
	} catch (err) {
		// ignore
	}
	socket.connect();
}

function recoverOnVisibility() {
	if (!socket || !getStoredToken()) {
		return;
	}

	syncSocketAuthToken();

	if (!socket.connected) {
		socketLog("visible, connecting");
		socket.connect();
		return;
	}

	const hiddenFor =
		hiddenAt != null ? Date.now() - hiddenAt : FORCE_RECONNECT_HIDDEN_MS + 1;

	if (hiddenFor >= FORCE_RECONNECT_HIDDEN_MS) {
		forceReconnect("visibility");
		return;
	}

	// Short background: keep connection, ask UI to re-join rooms + soft refetch.
	dispatchRecover("visibility");
}

function onVisibilityChange() {
	if (typeof document === "undefined") {
		return;
	}

	if (document.visibilityState === "hidden") {
		hiddenAt = Date.now();
		return;
	}

	if (document.visibilityState === "visible") {
		recoverOnVisibility();
		hiddenAt = null;
	}
}

function onWindowOnline() {
	if (!socket || !getStoredToken()) {
		return;
	}
	if (!socket.connected) {
		syncSocketAuthToken();
		socketLog("online, connecting");
		socket.connect();
		return;
	}
	forceReconnect("online");
}

function attachVisibilityHandler() {
	if (visibilityHandlerAttached || typeof document === "undefined") {
		return;
	}

	visibilityHandlerAttached = true;
	document.addEventListener("visibilitychange", onVisibilityChange);
	if (typeof window !== "undefined") {
		window.addEventListener("online", onWindowOnline);
	}
}

function detachVisibilityHandler() {
	if (!visibilityHandlerAttached || typeof document === "undefined") {
		return;
	}

	document.removeEventListener("visibilitychange", onVisibilityChange);
	if (typeof window !== "undefined") {
		window.removeEventListener("online", onWindowOnline);
	}
	visibilityHandlerAttached = false;
}

function createSocket() {
	const backendUrl = getBackendUrl();
	const token = getStoredToken();

	const options = {
		transports: ["websocket", "polling"],
		reconnection: true,
		reconnectionAttempts: Infinity,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 5000,
		timeout: 20000,
	};

	if (token) {
		options.query = { token };
	}

	const instance = openSocket(backendUrl, options);
	attachManagerListeners(instance);
	attachVisibilityHandler();
	return instance;
}

function connectToSocket() {
	if (!getStoredToken()) {
		return null;
	}

	if (!socket) {
		socket = createSocket();
	} else if (!socket.connected) {
		syncSocketAuthToken();
		socket.connect();
	}

	return socket;
}

export function disconnectSocket() {
	if (socket) {
		socket.removeAllListeners();
		socket.disconnect();
		socket = null;
	}
	detachVisibilityHandler();
	managerListenersAttached = false;
	hadDisconnect = false;
	hiddenAt = null;
	lastRecoverAt = 0;
}

export default connectToSocket;
